import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatPrice as formatCurrencyPrice } from '../utils/currencyUtils';
import { FaSearch, FaFilter, FaTimes, FaHeart, FaRegHeart, FaStar, FaMapMarkerAlt, FaClock, FaChevronDown, FaChevronUp, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { FiArrowUpRight } from 'react-icons/fi';
import * as experienceService from '../services/experienceService';
import MainLayout from '../components/Layout/MainLayout';

/** Resolved outside the component so hero video `load()` effect can depend on URL safely */
function getHeroBgVideoUrl(experience) {
    if (!experience?.videos) return null;
    const v = experience.videos;
    if (v.coverVideo) return v.coverVideo.url || v.coverVideo;
    if (v.horizontalVideo?.url) return v.horizontalVideo.url;
    if (v.verticalVideo?.url) return v.verticalVideo.url;
    if (Array.isArray(v) && v.length > 0) return v[0].url || v[0];
    if (experience.videos?.verticalVideo?.url) return experience.videos.verticalVideo.url;
    if (experience.videos?.coverVideo) return experience.videos.coverVideo.url || experience.videos.coverVideo;
    if (experience.videos && experience.videos.length > 0) {
        const video = experience.videos[0];
        return video.url || video;
    }
    return null;
}

const ExperiencesPage = () => {
    const navigate = useNavigate();
    const { countryId } = useParams();

    // State management
    const [experiences, setExperiences] = useState([]);
    const [filteredExperiences, setFilteredExperiences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [favorites, setFavorites] = useState([]);

    // Filter states
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedDurations, setSelectedDurations] = useState([]);
    const [selectedPriceRange, setSelectedPriceRange] = useState('');
    const [sortBy, setSortBy] = useState('latest');

    // Collapsible section states
    const [expandedSections, setExpandedSections] = useState({
        categories: true,
        duration: true,
        price: true
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const observerTarget = useRef(null);

    // Hero carousel (featured experiences + fallback to first page)
    const [heroExperiences, setHeroExperiences] = useState([]);
    const [heroSlideIndex, setHeroSlideIndex] = useState(0);
    const heroVideoRef = useRef(null);
    const [heroMediaOpacity, setHeroMediaOpacity] = useState(1);
    const heroFirstPaint = useRef(true);

    const playHeroVideoEl = useCallback((el) => {
        if (!el || el.tagName !== 'VIDEO') return;
        el.muted = true;
        el.defaultMuted = true;
        const p = el.play();
        if (p !== undefined && typeof p.catch === 'function') {
            p.catch(() => {
                /* autoplay / decode blocked — retry once */
                requestAnimationFrame(() => {
                    el.play?.().catch(() => { });
                });
            });
        }
    }, []);

    // Filter options
    const categories = [
        'Adventure Tours',
        'Cultural Experiences',
        'Sports & Fitness',
        'Wildlife & Conservation',
        'Luxury Travel',
        'Space Travel',
        'Culinary Experiences',
        'Wellness & Spa',
        'Photography Tours',
        'Educational Tours'
    ];

    const durations = [
        { value: '1-3', label: '1-3 hours' },
        { value: '4-8', label: '4-8 hours' },
        { value: '24-72', label: '1-3 days' },
        { value: '96-168', label: '4-7 days' },
        { value: '192-336', label: '8-14 days' },
        { value: '360+', label: '15+ days' }
    ];

    const priceRanges = [
        { value: 'under-500', label: 'Under 500' },
        { value: '500-1000', label: '500 - 1,000' },
        { value: '1000-2500', label: '1,000 - 2,500' },
        { value: '2500-5000', label: '2,500 - 5,000' },
        { value: 'over-5000', label: 'Over 5,000' }
    ];

    // Load experiences
    useEffect(() => {
        loadExperiences();
    }, [countryId, currentPage]);

    // Hero: prefer featured list for carousel backgrounds
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await experienceService.getFeaturedExperiences(10);
                if (cancelled) return;
                if (res?.success && Array.isArray(res?.data) && res.data.length > 0) {
                    setHeroExperiences(res.data);
                }
            } catch {
                /* fallback to main list below */
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const heroSlides = useMemo(() => {
        const fromFeatured = heroExperiences.filter(Boolean);
        if (fromFeatured.length > 0) return fromFeatured;
        return (experiences || []).filter(Boolean).slice(0, 10);
    }, [heroExperiences, experiences]);

    const heroCount = heroSlides.length;
    const activeHeroIndex = heroCount ? heroSlideIndex % heroCount : 0;
    const currentHero = heroCount ? heroSlides[activeHeroIndex] : null;
    const nextHero = heroCount > 1 ? heroSlides[(activeHeroIndex + 1) % heroCount] : null;

    useEffect(() => {
        if (heroCount < 2) return;
        const t = setInterval(() => {
            setHeroSlideIndex((i) => (i + 1) % heroCount);
        }, 20000);
        return () => clearInterval(t);
    }, [heroCount]);

    const heroVisualKey = currentHero?._id ?? 'empty';

    useLayoutEffect(() => {
        if (heroFirstPaint.current) {
            heroFirstPaint.current = false;
            return;
        }
        setHeroMediaOpacity(0);
        const t = window.setTimeout(() => setHeroMediaOpacity(1), 100);
        return () => window.clearTimeout(t);
    }, [heroVisualKey]);

    const heroBgVideoUrlForHook = currentHero ? getHeroBgVideoUrl(currentHero) : null;

    useEffect(() => {
        const v = heroVideoRef.current;
        if (!v || !heroBgVideoUrlForHook) return;

        let cancelled = false;
        const kick = () => {
            if (!cancelled) playHeroVideoEl(v);
        };

        try {
            v.load();
        } catch {
            /* ignore */
        }

        v.addEventListener('loadeddata', kick);
        v.addEventListener('canplay', kick);
        v.addEventListener('stalled', kick);
        v.addEventListener('waiting', kick);

        kick();
        const softRetry = window.setTimeout(kick, 500);

        return () => {
            cancelled = true;
            window.clearTimeout(softRetry);
            v.removeEventListener('loadeddata', kick);
            v.removeEventListener('canplay', kick);
            v.removeEventListener('stalled', kick);
            v.removeEventListener('waiting', kick);
        };
    }, [heroBgVideoUrlForHook, currentHero?._id, playHeroVideoEl]);

    // Load favorites from localStorage
    useEffect(() => {
        const stored = localStorage.getItem('travelAgent_favoriteExperiences');
        if (stored) {
            try {
                setFavorites(JSON.parse(stored));
            } catch (e) {
                console.error('Error loading favorites:', e);
            }
        }
    }, []);

    // Save favorites to localStorage
    useEffect(() => {
        localStorage.setItem('travelAgent_favoriteExperiences', JSON.stringify(favorites));
    }, [favorites]);

    const loadExperiences = async () => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                page: currentPage,
                limit: 12,
                status: 'active'
            };

            let response;
            if (countryId) {
                response = await experienceService.getExperiencesByLocation(countryId, params);
            } else {
                response = await experienceService.getAllExperiences(params);
            }

            if (response.success && response.data) {
                if (currentPage === 1) {
                    setExperiences(response.data);
                } else {
                    setExperiences(prev => [...prev, ...response.data]);
                }
                setHasMore(response.pagination?.hasNextPage || false);
            } else {
                setError('Failed to load experiences');
            }
        } catch (err) {
            console.error('Error loading experiences:', err);
            setError(err.message || 'Failed to load experiences');
        } finally {
            setLoading(false);
        }
    };

    // Filter experiences
    useEffect(() => {
        let filtered = [...experiences];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(exp =>
                exp.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                exp.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                exp.location?.country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                exp.location?.city?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Category filter
        if (selectedCategories.length > 0) {
            filtered = filtered.filter(exp => selectedCategories.includes(exp.category));
        }

        // Duration filter
        if (selectedDurations.length > 0) {
            filtered = filtered.filter(exp => {
                const durationHours = getDurationInHours(exp.duration);
                return selectedDurations.some(range => {
                    const [min, max] = range.split('-').map(v => v === '+' ? Infinity : parseInt(v));
                    return durationHours >= min && (max ? durationHours <= max : true);
                });
            });
        }

        // Price filter - use total price (base + commission)
        if (selectedPriceRange) {
            filtered = filtered.filter(exp => {
                // Use detailTotal/listingTotal if available (includes commission), otherwise basePrice
                const price = exp.detailTotal || exp.listingTotal || exp.pricing?.basePrice || 0;
                switch (selectedPriceRange) {
                    case 'under-500': return price < 500;
                    case '500-1000': return price >= 500 && price <= 1000;
                    case '1000-2500': return price >= 1000 && price <= 2500;
                    case '2500-5000': return price >= 2500 && price <= 5000;
                    case 'over-5000': return price > 5000;
                    default: return true;
                }
            });
        }

        // Sort - use total price (base + commission)
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'price-low':
                    const priceA = a.detailTotal || a.listingTotal || a.pricing?.basePrice || 0;
                    const priceB = b.detailTotal || b.listingTotal || b.pricing?.basePrice || 0;
                    return priceA - priceB;
                case 'price-high':
                    const priceAHigh = a.detailTotal || a.listingTotal || a.pricing?.basePrice || 0;
                    const priceBHigh = b.detailTotal || b.listingTotal || b.pricing?.basePrice || 0;
                    return priceBHigh - priceAHigh;
                case 'rating':
                    return (b.averageRating || 0) - (a.averageRating || 0);
                case 'latest':
                default:
                    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
            }
        });

        setFilteredExperiences(filtered);
    }, [experiences, searchTerm, selectedCategories, selectedDurations, selectedPriceRange, sortBy]);

    // Intersection observer for infinite scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loading) {
                    setCurrentPage(prev => prev + 1);
                }
            },
            { threshold: 1 }
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => observer.disconnect();
    }, [hasMore, loading]);

    // Helper functions
    const getDurationInHours = (duration) => {
        if (!duration) return 0;
        if (duration.hours) return duration.hours;
        if (duration.days) return duration.days * 24;
        if (duration.minutes) return duration.minutes / 60;
        return 0;
    };

    const formatDuration = (duration) => {
        if (!duration) return 'Duration varies';
        if (duration.days) return `${duration.days} ${duration.days === 1 ? 'day' : 'days'}`;
        if (duration.hours) return `${duration.hours}h`;
        if (duration.minutes) return `${duration.minutes}m`;
        return 'Duration varies';
    };

    const formatPrice = (pricing, experience) => {
        if (!pricing?.basePrice && !experience?.detailTotal && !experience?.listingTotal) return 'Price on request';
        const displayPrice = experience?.detailTotal || experience?.listingTotal || pricing?.basePrice || 0;
        return formatCurrencyPrice(displayPrice, pricing?.currency);
    };

    const getCoverImage = (experience) => {
        if (experience.images && experience.images.length > 0) {
            const coverImage = experience.images.find(img => img.isCover) || experience.images[0];
            return coverImage.url || coverImage;
        }
        return 'https://via.placeholder.com/400x300?text=No+Image';
    };

    const getVerticalVideo = (experience) => {
        if (experience.videos?.verticalVideo?.url) {
            return experience.videos.verticalVideo.url;
        }
        if (experience.videos?.coverVideo) {
            return experience.videos.coverVideo.url || experience.videos.coverVideo;
        }
        if (experience.videos && experience.videos.length > 0) {
            const video = experience.videos[0];
            return video.url || video;
        }
        return null;
    };

    const getHeroBackgroundVideo = (experience) => getHeroBgVideoUrl(experience);

    const getHeroSubtitle = (experience) => {
        if (!experience) return '';
        if (experience.category) return experience.category;
        const city = experience.location?.city;
        const country = experience.location?.country;
        if (city && country) return `${city} · ${country}`;
        if (country) return country;
        return 'Curated for you';
    };

    const goHeroPrev = () => {
        if (!heroCount) return;
        setHeroSlideIndex((i) => (i - 1 + heroCount) % heroCount);
    };

    const goHeroNext = () => {
        if (!heroCount) return;
        setHeroSlideIndex((i) => (i + 1) % heroCount);
    };

    const toggleFavorite = (experienceId) => {
        setFavorites(prev => {
            if (prev.includes(experienceId)) {
                return prev.filter(id => id !== experienceId);
            } else {
                return [...prev, experienceId];
            }
        });
    };

    const isFavorite = (experienceId) => favorites.includes(experienceId);

    const handleFilterChange = (filterType, value) => {
        switch (filterType) {
            case 'category':
                setSelectedCategories(prev =>
                    prev.includes(value) ? prev.filter(c => c !== value) : [...prev, value]
                );
                break;
            case 'duration':
                setSelectedDurations(prev =>
                    prev.includes(value) ? prev.filter(d => d !== value) : [...prev, value]
                );
                break;
            case 'price':
                setSelectedPriceRange(value);
                break;
            default:
                break;
        }
    };

    const clearFilters = () => {
        setSelectedCategories([]);
        setSelectedDurations([]);
        setSelectedPriceRange('');
        setSearchTerm('');
    };

    // Toggle collapsible sections
    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Mobile filter toggle
    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const heroFallbackImage = 'https://images.pexels.com/photos/3408354/pexels-photo-3408354.jpeg?auto=compress&cs=tinysrgb&w=1600&h=900&dpr=1';
    const heroBgVideoUrl = currentHero ? getHeroBackgroundVideo(currentHero) : null;
    const heroPoster = currentHero ? getCoverImage(currentHero) : heroFallbackImage;

    return (
        <MainLayout>
            <div className="min-h-screen" style={{ backgroundColor: '#f7f7f7' }}>

                {/* ── Hero ── */}
                <section className="relative overflow-hidden bg-white" style={{ minHeight: 'min(62svh, 36rem)' }}>

                    {/* Right panel: full-height video / image */}
                    <div className="hidden lg:block absolute top-0 right-0 bottom-0 w-[55%] z-0 overflow-hidden"
                        style={{ clipPath: 'polygon(10% 0, 100% 0, 100% 100%, 0% 100%)' }}>
                        <div className="absolute inset-0 transition-opacity duration-700 ease-out" style={{ opacity: heroMediaOpacity }}>
                            {heroBgVideoUrl ? (
                                <video
                                    ref={heroVideoRef}
                                    className="w-full h-full object-cover pointer-events-none"
                                    src={heroBgVideoUrl}
                                    poster={heroPoster}
                                    autoPlay muted loop playsInline preload="auto"
                                    onLoadedData={(e) => playHeroVideoEl(e.currentTarget)}
                                    onCanPlay={(e) => playHeroVideoEl(e.currentTarget)}
                                    onStalled={(e) => playHeroVideoEl(e.currentTarget)}
                                />
                            ) : (
                                <img src={heroPoster} alt="" className="w-full h-full object-cover" />
                            )}
                        </div>

                        {/* Floating experience card */}
                        {currentHero && (
                            <div className="absolute bottom-8 right-7 z-10">
                                <div className="bg-white/90 backdrop-blur-sm border border-white/60 px-5 py-4 max-w-[240px] overflow-hidden" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                                    <p className="text-[9px] tracking-[0.3em] uppercase font-poppinsLight text-gray-400 mb-1.5 truncate">Now Featuring</p>
                                    <p className="font-AXONALLPrimeRegular uppercase text-sm tracking-wide leading-tight mb-1 text-gray-900 line-clamp-2">{currentHero.title}</p>
                                    <p className="text-[9px] tracking-[0.2em] uppercase font-poppinsLight text-gray-400 truncate">{getHeroSubtitle(currentHero)}</p>
                                </div>
                            </div>
                        )}

                        {/* Slide nav */}
                        {heroCount > 1 && (
                            <div className="absolute bottom-8 left-6 z-10 flex items-center gap-1">
                                <button type="button" onClick={goHeroPrev}
                                    className="p-2 bg-white/80 backdrop-blur-sm text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200"
                                    aria-label="Previous">
                                    <FaChevronLeft size={10} />
                                </button>
                                <button type="button" onClick={goHeroNext}
                                    className="p-2 bg-white/80 backdrop-blur-sm text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200"
                                    aria-label="Next">
                                    <FaChevronRight size={10} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Mobile background */}
                    <div className="lg:hidden absolute inset-0 z-0">
                        <div className="absolute inset-0 transition-opacity duration-700" style={{ opacity: heroMediaOpacity }}>
                            {heroBgVideoUrl ? (
                                <video ref={heroVideoRef} className="w-full h-full object-cover pointer-events-none"
                                    src={heroBgVideoUrl} poster={heroPoster} autoPlay muted loop playsInline />
                            ) : (
                                <img src={heroPoster} alt="" className="w-full h-full object-cover" />
                            )}
                        </div>
                        <div className="absolute inset-0 bg-black/65" />
                    </div>

                    {/* Left content panel */}
                    <div className="relative z-10 lg:w-[50%] flex flex-col">

                        {/* Top strip */}
                        <div className="border-b border-white/20 lg:border-gray-100 px-6 sm:px-8 lg:px-12 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <FaStar className="w-3.5 h-3.5 text-white/50 lg:text-gray-400" aria-hidden />
                                <span className="text-[9px] tracking-[0.35em] uppercase font-poppinsLight text-white/50 lg:text-gray-400">
                                    Axonall Experiences
                                </span>
                            </div>
                            {heroCount > 1 && (
                                <span className="text-[9px] tracking-[0.25em] font-poppinsLight text-white/35 lg:text-gray-300">
                                    {String(activeHeroIndex + 1).padStart(2, '0')} / {String(heroCount).padStart(2, '0')}
                                </span>
                            )}
                        </div>

                        {/* Main text */}
                        <div className="flex-1 flex flex-col justify-center px-6 sm:px-8 lg:px-12 py-6">

                            <div className="flex items-center gap-3 mb-4 overflow-hidden">
                                <div className="w-6 h-px bg-white/30 lg:bg-gray-300 shrink-0" />
                                <span className="text-[9px] tracking-[0.2em] uppercase font-poppinsLight text-white/50 lg:text-gray-400 min-w-0 truncate">
                                    {currentHero ? getHeroSubtitle(currentHero) : 'Curated Experiences · Axonall Reserve'}
                                </span>
                            </div>

                            <h1 className="font-AXONALLPrimeRegular uppercase tracking-wide text-white lg:text-gray-900 mb-4 overflow-hidden"
                                style={{ fontSize: 'clamp(1.6rem, 3vw, 2.6rem)', lineHeight: '1.2' }}>
                                {currentHero ? (
                                    <span className="block line-clamp-3">{currentHero.title}</span>
                                ) : (
                                    <>
                                        <span className="block">Extraordinary.</span>
                                        <span className="block">By Design.</span>
                                    </>
                                )}
                            </h1>

                            <div className="flex gap-4 mb-5">
                                <div className="w-px bg-white/25 lg:bg-gray-200 shrink-0 mt-1" />
                                <p className="text-white/65 lg:text-gray-500 font-poppinsLight text-sm leading-relaxed max-w-xs">
                                    Hand-picked journeys crafted for discerning travelers — from adventure expeditions to intimate cultural immersions.
                                </p>
                            </div>

                            <div className="flex flex-wrap gap-3 mb-5">
                                {currentHero ? (
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/experience/${currentHero._id}`)}
                                        className="inline-flex items-center gap-2.5 px-6 py-3 lg:px-7 lg:py-3.5 bg-white text-gray-900 lg:bg-gray-900 lg:text-white text-[10px] tracking-[0.25em] uppercase font-poppinsRegular hover:opacity-90 transition-all duration-200"
                                    >
                                        Book Featured
                                        <FiArrowUpRight className="text-sm" strokeWidth={2.5} />
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => document.getElementById('experiences-grid')?.scrollIntoView({ behavior: 'smooth' })}
                                        className="inline-flex items-center gap-2.5 px-6 py-3 lg:px-7 lg:py-3.5 bg-white text-gray-900 lg:bg-gray-900 lg:text-white text-[10px] tracking-[0.25em] uppercase font-poppinsRegular hover:opacity-90 transition-all duration-200"
                                    >
                                        Browse Experiences
                                        <FiArrowUpRight className="text-sm" strokeWidth={2.5} />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => document.getElementById('experiences-grid')?.scrollIntoView({ behavior: 'smooth' })}
                                    className="inline-flex items-center gap-2.5 px-6 py-3 lg:px-7 lg:py-3.5 border border-white/50 text-white lg:border-gray-300 lg:text-gray-700 text-[10px] tracking-[0.25em] uppercase font-poppinsRegular hover:border-white hover:lg:border-gray-900 hover:lg:text-gray-900 transition-all duration-200"
                                >
                                    Browse All
                                </button>
                            </div>

                        </div>

                        {/* Bottom strip */}
                        <div className="border-t border-white/20 lg:border-gray-100 px-6 sm:px-8 lg:px-12 py-3 flex items-center justify-end gap-6">
                            {heroCount > 1 && (
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {heroSlides.map((_, idx) => (
                                        <button key={idx} type="button" onClick={() => setHeroSlideIndex(idx)}
                                            aria-label={`Experience ${idx + 1}`}
                                            className={`transition-all duration-300 ${idx === activeHeroIndex ? 'w-5 h-[2px] bg-white lg:bg-gray-900' : 'w-[2px] h-[2px] bg-white/35 lg:bg-gray-300 rounded-full hover:bg-white/60 lg:hover:bg-gray-500'}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* ── Search & sort bar ── */}
                <div className="bg-white border-b border-gray-200">
                    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 py-5 flex flex-col gap-4">
                        <div className="relative max-w-3xl w-full">
                            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                            <input
                                type="text"
                                placeholder="Search experiences, destinations, activities..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-11 pr-4 py-3 border border-gray-200 bg-white text-gray-900 placeholder-gray-400 font-poppinsLight text-sm focus:outline-none focus:border-gray-900 transition-colors duration-200"
                            />
                        </div>
                        <div className="flex flex-wrap gap-3 items-center">
                            <div className="border border-gray-200 bg-white px-3 py-2">
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="bg-transparent border-0 text-gray-700 text-[11px] tracking-[0.15em] uppercase font-poppinsRegular focus:outline-none cursor-pointer pr-4"
                                >
                                    <option value="latest">Latest experiences</option>
                                    <option value="price-low">Price: Low to High</option>
                                    <option value="price-high">Price: High to Low</option>
                                    <option value="rating">Highest rated</option>
                                </select>
                            </div>
                            {(selectedCategories.length > 0 || selectedDurations.length > 0 || selectedPriceRange) && (
                                <button type="button" onClick={clearFilters}
                                    className="border border-gray-200 bg-white px-4 py-2 text-[11px] tracking-[0.15em] uppercase font-poppinsRegular text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors duration-200">
                                    Clear filters
                                </button>
                            )}
                            <button type="button" onClick={toggleSidebar}
                                className="lg:hidden inline-flex items-center gap-2 border border-gray-200 bg-white px-4 py-2 text-[11px] tracking-[0.15em] uppercase font-poppinsRegular text-gray-700">
                                <FaFilter size={11} />
                                Filters
                            </button>
                            <span className="text-[11px] tracking-[0.15em] uppercase text-gray-400 font-poppinsLight ml-auto">
                                {filteredExperiences.length} {filteredExperiences.length === 1 ? 'experience' : 'experiences'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* ── Body: sidebar + grid ── */}
                <div id="experiences-grid" className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 py-8">
                    <div className="flex gap-8">

                        {/* Filters sidebar */}
                        <aside
                            className={`${isSidebarOpen ? 'fixed inset-0 z-50 bg-black/60 backdrop-blur-sm lg:relative lg:bg-transparent' : 'hidden lg:block'} lg:w-72 flex-shrink-0`}
                            onClick={(e) => { if (e.target === e.currentTarget) setIsSidebarOpen(false); }}
                        >
                            <div className={`${isSidebarOpen ? 'fixed right-0 top-0 bottom-0 w-80 bg-white overflow-y-auto border-l border-gray-200' : ''} lg:sticky lg:top-4 bg-white border border-gray-200`}
                                style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>

                                <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex justify-between items-center">
                                    <div>
                                        <p className="text-[10px] tracking-[0.3em] uppercase font-poppinsLight text-gray-400 mb-0.5">Refine</p>
                                        <h3 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 text-lg">Filters</h3>
                                    </div>
                                    {isSidebarOpen && (
                                        <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors">
                                            <FaTimes size={16} />
                                        </button>
                                    )}
                                </div>

                                <div className="p-4 space-y-1">
                                    {/* Helper to render a filter section */}
                                    {[
                                        {
                                            key: 'categories', label: 'Categories',
                                            content: (
                                                <div className="px-5 pb-5 space-y-3 max-h-64 overflow-y-auto border-t border-gray-100">
                                                    <div className="pt-3" />
                                                    {categories.map(category => (
                                                        <label key={category} className="flex items-center cursor-pointer group">
                                                            <div className="relative">
                                                                <input type="checkbox" checked={selectedCategories.includes(category)}
                                                                    onChange={() => handleFilterChange('category', category)} className="sr-only peer" />
                                                                <div className="w-4 h-4 border border-gray-300 bg-white peer-checked:bg-gray-900 peer-checked:border-gray-900 transition-all flex items-center justify-center">
                                                                    {selectedCategories.includes(category) && (
                                                                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <span className={`ml-3 text-xs font-poppinsLight transition-colors ${selectedCategories.includes(category) ? 'text-gray-900 font-poppinsRegular' : 'text-gray-500 group-hover:text-gray-900'}`}>
                                                                {category}
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            )
                                        },
                                        {
                                            key: 'duration', label: 'Duration',
                                            content: (
                                                <div className="px-5 pb-5 space-y-3 border-t border-gray-100">
                                                    <div className="pt-3" />
                                                    {durations.map(d => (
                                                        <label key={d.value} className="flex items-center cursor-pointer group">
                                                            <div className="relative">
                                                                <input type="checkbox" checked={selectedDurations.includes(d.value)}
                                                                    onChange={() => handleFilterChange('duration', d.value)} className="sr-only peer" />
                                                                <div className="w-4 h-4 border border-gray-300 bg-white peer-checked:bg-gray-900 peer-checked:border-gray-900 transition-all flex items-center justify-center">
                                                                    {selectedDurations.includes(d.value) && (
                                                                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <span className={`ml-3 text-xs font-poppinsLight transition-colors ${selectedDurations.includes(d.value) ? 'text-gray-900 font-poppinsRegular' : 'text-gray-500 group-hover:text-gray-900'}`}>
                                                                {d.label}
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            )
                                        },
                                        {
                                            key: 'price', label: 'Price Range',
                                            content: (
                                                <div className="px-5 pb-5 space-y-3 border-t border-gray-100">
                                                    <div className="pt-3" />
                                                    {priceRanges.map(r => (
                                                        <label key={r.value} className="flex items-center cursor-pointer group">
                                                            <div className="relative">
                                                                <input type="radio" name="priceRange" checked={selectedPriceRange === r.value}
                                                                    onChange={() => handleFilterChange('price', r.value)} className="sr-only peer" />
                                                                <div className="w-4 h-4 border border-gray-300 rounded-full bg-white peer-checked:bg-gray-900 peer-checked:border-gray-900 transition-all flex items-center justify-center">
                                                                    {selectedPriceRange === r.value && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                                                                </div>
                                                            </div>
                                                            <span className={`ml-3 text-xs font-poppinsLight transition-colors ${selectedPriceRange === r.value ? 'text-gray-900 font-poppinsRegular' : 'text-gray-500 group-hover:text-gray-900'}`}>
                                                                {r.label}
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            )
                                        }
                                    ].map(({ key, label, content }) => (
                                        <div key={key} className="border border-gray-100 overflow-hidden">
                                            <button onClick={() => toggleSection(key)}
                                                className="w-full px-5 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors">
                                                <span className="text-[10px] tracking-[0.25em] uppercase font-poppinsRegular text-gray-700">{label}</span>
                                                {expandedSections[key] ? <FaChevronUp className="text-gray-400" size={11} /> : <FaChevronDown className="text-gray-400" size={11} />}
                                            </button>
                                            {expandedSections[key] && content}
                                        </div>
                                    ))}

                                    {(selectedCategories.length > 0 || selectedDurations.length > 0 || selectedPriceRange) && (
                                        <div className="pt-3 pb-1">
                                            <button onClick={clearFilters}
                                                className="w-full py-3.5 bg-gray-900 text-white text-[10px] tracking-[0.25em] uppercase font-poppinsRegular hover:bg-black transition-colors duration-200">
                                                Reset Filters
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </aside>

                        {/* Main content */}
                        <main className="flex-1">
                            {error && (
                                <div className="bg-red-50 border-l-2 border-red-500 text-red-700 px-5 py-4 mb-6">
                                    <span className="text-sm font-poppinsMedium">⚠️ {error}</span>
                                </div>
                            )}

                            {loading && currentPage === 1 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                    {[...Array(6)].map((_, i) => (
                                        <div key={i} className="bg-white overflow-hidden animate-pulse border border-gray-100">
                                            <div className="h-52 bg-gray-100" />
                                            <div className="p-4 space-y-2.5">
                                                <div className="h-4 bg-gray-100 w-3/4" />
                                                <div className="h-3 bg-gray-100 w-1/2" />
                                                <div className="h-3 bg-gray-100 w-2/3" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : filteredExperiences.length === 0 ? (
                                <div className="text-center py-20 bg-white border border-gray-200">
                                    <FaStar className="text-gray-200 mx-auto mb-5" size={36} />
                                    <p className="text-[10px] tracking-[0.3em] uppercase font-poppinsLight text-gray-400 mb-2">No Results</p>
                                    <h3 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 text-xl mb-4">No Experiences Found</h3>
                                    <p className="text-gray-400 font-poppinsLight text-sm mb-8 max-w-sm mx-auto">
                                        No experiences match your current filters. Try adjusting your search.
                                    </p>
                                    <button onClick={clearFilters}
                                        className="px-8 py-3 bg-gray-900 text-white text-[10px] tracking-[0.25em] uppercase font-poppinsRegular hover:bg-black transition-colors duration-200">
                                        Clear All Filters
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                        {filteredExperiences.map(experience => (
                                            <div
                                                key={experience._id}
                                                className="relative h-[340px] sm:h-[320px] bg-black overflow-hidden group cursor-pointer"
                                                style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}
                                                onClick={() => navigate(`/experience/${experience._id}`)}
                                            >
                                                {/* Background: image + hover video */}
                                                <div className="absolute inset-0 z-0">
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent z-10" />
                                                    {getVerticalVideo(experience) ? (
                                                        <>
                                                            <img src={getCoverImage(experience)} alt={experience.title}
                                                                className="w-full h-full object-cover transition-all duration-700 group-hover:opacity-0" />
                                                            <video src={getVerticalVideo(experience)}
                                                                className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700 delay-100"
                                                                autoPlay loop muted playsInline />
                                                        </>
                                                    ) : (
                                                        <img src={getCoverImage(experience)} alt={experience.title}
                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                                    )}
                                                </div>

                                                {/* Top: badges + favorite */}
                                                <div className="absolute top-3 left-3 right-3 z-20 flex justify-between items-start">
                                                    <div className="flex flex-wrap gap-1.5 max-w-[78%]">
                                                        {experience.category && (
                                                            <div className="px-2 py-1 bg-black/50 backdrop-blur-sm text-[8px] tracking-[0.2em] uppercase font-poppinsRegular text-white border border-white/10">
                                                                {experience.category}
                                                            </div>
                                                        )}
                                                        {experience.location?.city && (
                                                            <div className="px-2 py-1 bg-white/15 backdrop-blur-sm text-[8px] tracking-[0.2em] uppercase font-poppinsRegular text-white border border-white/10">
                                                                {experience.location.city}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button onClick={(e) => { e.stopPropagation(); toggleFavorite(experience._id); }}
                                                        className="p-2 bg-white/15 backdrop-blur-sm border border-white/20 text-white hover:bg-white hover:text-black transition-all shrink-0">
                                                        {isFavorite(experience._id) ? <FaHeart className="text-red-400" size={13} /> : <FaRegHeart size={13} />}
                                                    </button>
                                                </div>

                                                {/* Bottom: content */}
                                                <div className="absolute bottom-0 left-0 right-0 p-5 z-20">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        {experience.averageRating > 0 && (
                                                            <div className="flex items-center gap-1 bg-white/15 backdrop-blur-sm border border-white/15 px-2.5 py-1">
                                                                <FaStar className="text-yellow-400" size={10} />
                                                                <span className="text-[10px] font-poppinsRegular text-white">{experience.averageRating.toFixed(1)}</span>
                                                            </div>
                                                        )}
                                                        <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/10 px-2.5 py-1">
                                                            <FaClock className="text-white/50" size={9} />
                                                            <span className="text-[10px] font-poppinsLight text-white/80">{formatDuration(experience.duration)}</span>
                                                        </div>
                                                    </div>

                                                    <h3 className="font-AXONALLPrimeRegular uppercase tracking-wide text-white text-lg leading-snug mb-3 line-clamp-2">
                                                        {experience.title}
                                                    </h3>

                                                    <div className="flex items-end justify-between gap-2">
                                                        <div className="min-w-0">
                                                            <span className="text-[9px] tracking-[0.18em] uppercase font-poppinsLight text-white/40 block mb-0.5">Starting from</span>
                                                            <div className="font-AXONALLPrimeRegular text-white text-lg truncate">
                                                                {formatPrice(experience.pricing, experience)}
                                                            </div>
                                                        </div>
                                                        <button
                                                            className="shrink-0 px-3 py-2 bg-white text-black text-[9px] tracking-[0.2em] uppercase font-poppinsRegular opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 hover:bg-gray-100"
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/experience/${experience._id}`); }}
                                                        >
                                                            Book
                                                            <FiArrowUpRight className="inline ml-1" strokeWidth={2} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {hasMore && (
                                        <div ref={observerTarget} className="flex justify-center py-12">
                                            {loading && (
                                                <div className="flex items-center gap-2">
                                                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                                                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                                                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                                                    <span className="ml-2 text-[11px] tracking-[0.2em] uppercase font-poppinsLight text-gray-400">Loading</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </main>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default ExperiencesPage;
